import React, { useState, useRef, useEffect } from "react";
import "../../styles/utilities/language-dropdown.scss";
import { RiTranslate2 } from "react-icons/ri";
import { FaChevronDown } from "react-icons/fa";

interface LanguageOption {
  label: string;
  description: string;
}

interface LanguageDropdownProps {
  options: LanguageOption[];
  defaultLanguage?: string;
  onSelect: (selected: string) => void;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  options,
  defaultLanguage = "English",
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (option: LanguageOption) => {
    setSelectedLanguage(option.label);
    setIsOpen(false);
    onSelect(option.label);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`language-dropdown-container ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button 
        className="language-dropdown-button" 
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <RiTranslate2 className="language-icon" />
        <span className="language-text">{selectedLanguage}</span>
        <FaChevronDown 
          className={`arrow-icon ${isOpen ? 'rotated' : ''}`} 
        />
      </button>
      
      <ul 
        className="language-dropdown-menu" 
        role="listbox"
        aria-label="Select language"
      >
        {options.map((option, index) => (
          <li
            key={index}
            role="option"
            aria-selected={selectedLanguage === option.label}
            className={`language-option ${selectedLanguage === option.label ? 'active' : ''}`}
            onClick={() => handleSelect(option)}
          >
            <div className="language-name">{option.label}</div>
            <div className="language-description">{option.description}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageDropdown; 